import { useEffect, useState } from "react";
import { useVoiceSettings } from "../useVoiceSettings";
import { Switch } from "V2.0/common/switch";
import InputComponent from "V2.0/components/InputComponent";
import { Button } from "V2.0/common/button";
import { Radio, RadioGroup } from "V2.0/common/radio";

function RecordingEnableSwitch(props) {
  const { isRecordingEnabled, onToggleRecordingEnable, disabled } = props;

  return (
    <Switch
      color="emerald"
      checked={isRecordingEnabled}
      onClick={onToggleRecordingEnable}
      disabled={disabled}
    />
  );
}

function DisclaimerEnableSwitch(props) {
  const { isDisclaimerEnabled, onToggleDisclaimerEnable, disabled } = props;

  return (
    <Switch
      color="emerald"
      checked={isDisclaimerEnabled}
      onClick={onToggleDisclaimerEnable}
      disabled={disabled}
    />
  );
}

enum DisclaimerMode {
  Message = "Message",
  Recording = "Recording",
}

function DisclaimerSettings(props) {
  const { initialMessageSettings, onUpdate, errors, onUpdateError, locked } =
    props;

  const [disclaimerMode, setDisclaimerMode] = useState(null);

  useEffect(() => {
    if (Boolean(initialMessageSettings?.initialMessageText)) {
      setDisclaimerMode(DisclaimerMode.Message);
      return;
    }

    if (Boolean(initialMessageSettings?.initialMessageURL)) {
      setDisclaimerMode(DisclaimerMode.Recording);
      return;
    }
  }, [initialMessageSettings]);

  const isError = Object.values(errors).some((value) => Boolean(value));
  const isRecordingDisclaimerModeError =
    disclaimerMode === DisclaimerMode.Recording || !Boolean(disclaimerMode);
  const isMessageDisclaimerModeError =
    disclaimerMode === DisclaimerMode.Message || !Boolean(disclaimerMode);

  function handleToggleDisclaimerEnable() {
    if (isError && !initialMessageSettings.disclaimerEnabled) {
      onUpdateError({
        initialMessageText: null,
        initialMessageURL: null,
      });
    }
    onUpdate({
      disclaimerEnabled: !initialMessageSettings.disclaimerEnabled,
    });
  }

  function handleUpdateDisclaimerMessage(message) {
    if (Boolean(errors.initialMessageText) && Boolean(message)) {
      onUpdateError((prevErrors) => {
        return {
          ...prevErrors,
          initialMessageText: null,
        };
      });
    }

    onUpdate({
      initialMessageText: message,
    });
  }

  function handleToggleDisclaimerMessageURL(url) {
    if (Boolean(errors.initialMessageURL) && Boolean(url)) {
      onUpdateError((prevErrors) => {
        return {
          ...prevErrors,
          initialMessageURL: null,
        };
      });
    }

    onUpdate({
      initialMessageURL: url,
    });
  }

  function handleDisclaimerModeChange(mode) {
    onUpdateError((prevErrors) => {
      return {
        initialMessageText: null,
        initialMessageURL: null,
      };
    });

    setDisclaimerMode(mode);
  }

  return (
    <div className="flex flex-col gap-y-4 px-6 w-full">
      <div className="flex flex-col gap-y-1">
        <div className="flex items-center gap-x-2">
          <p className="text-sm font-medium">Play Disclaimer</p>
          <DisclaimerEnableSwitch
            isDisclaimerEnabled={initialMessageSettings.disclaimerEnabled}
            onToggleDisclaimerEnable={handleToggleDisclaimerEnable}
            disabled={locked}
          />
        </div>
        <p className="text-sm mt-1 text-gray-500">
          Play pre-recorded disclaimer at the start of a call
        </p>
      </div>
      <div className="w-full">
        <RadioGroup
          defaultValue={disclaimerMode}
          disabled={!initialMessageSettings.disclaimerEnabled || locked}
        >
          <div className="ml-4 flex gap-x-2 items-start w-full">
            <Radio
              className="mt-1"
              value={DisclaimerMode.Message}
              onClick={(e) =>
                handleDisclaimerModeChange(DisclaimerMode.Message)
              }
            />
            <div className="w-full">
              <InputComponent
                disabled={
                  !initialMessageSettings.disclaimerEnabled ||
                  disclaimerMode === DisclaimerMode.Recording ||
                  locked
                }
                invalid={
                  disclaimerMode === DisclaimerMode.Message &&
                  initialMessageSettings.disclaimerEnabled &&
                  Boolean(errors.initialMessageText)
                }
                error={
                  isMessageDisclaimerModeError
                    ? errors.initialMessageText
                    : null
                }
                type="textArea"
                value={initialMessageSettings.initialMessageText}
                style={{ height: "100px", fontSize: "13px" }}
                label="Disclaimer Message"
                onChange={(e) => handleUpdateDisclaimerMessage(e.target.value)}
              />
            </div>
          </div>
          <div className="ml-4 flex gap-x-2 items-start w-full">
            <Radio
              className="mt-1"
              value={DisclaimerMode.Recording}
              onClick={(e) =>
                handleDisclaimerModeChange(DisclaimerMode.Recording)
              }
            />
            <div className="w-full">
              <InputComponent
                disabled={
                  !initialMessageSettings.disclaimerEnabled ||
                  disclaimerMode === DisclaimerMode.Message ||
                  locked
                }
                invalid={
                  disclaimerMode === DisclaimerMode.Recording &&
                  initialMessageSettings.disclaimerEnabled &&
                  Boolean(errors.initialMessageURL)
                }
                error={
                  isRecordingDisclaimerModeError
                    ? errors.initialMessageURL
                    : null
                }
                type="text"
                value={initialMessageSettings.initialMessageURL}
                label="Voice URL"
                onChange={(e) =>
                  handleToggleDisclaimerMessageURL(e.target.value)
                }
              />
            </div>
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}

function CallRecordingSettings(props) {
  const { recordingEnabled, workspaceId, assistantId, syncAssistant, locked } =
    props;

  const { updateVoiceSettings } = useVoiceSettings({
    syncAssistant,
    workspaceId,
    assistantId,
  });

  const [settings, setSettings] = useState({
    recordingEnabled,
  });

  function handleToggleRecordingEnabled() {
    const updatedSetting = {
      recordingEnabled: !recordingEnabled,
    };

    setSettings(updatedSetting);

    const lifecycleCbs = {
      onError: (e) => {
        setSettings({ recordingEnabled: recordingEnabled });
      },
    };

    updateVoiceSettings(updatedSetting, lifecycleCbs);
  }

  return (
    <div className="flex justify-between w-full border rounded-lg p-4 px-6 bg-gray-50">
      <div className="">
        <p className="font-semibold text-md">Record Calls</p>
        <p className="text-sm mt-1 text-gray-500">
          Record the calls handled by the assistant
        </p>
      </div>
      <div>
        <RecordingEnableSwitch
          isRecordingEnabled={settings.recordingEnabled}
          onToggleRecordingEnable={handleToggleRecordingEnabled}
          disabled={locked}
        />
      </div>
    </div>
  );
}

function CallDisclaimerSettings(props) {
  const {
    initialMessageSettings,
    workspaceId,
    assistantId,
    syncAssistant,
    locked,
  } = props;
  const { updateVoiceSettings } = useVoiceSettings({
    syncAssistant,
    workspaceId,
    assistantId,
  });

  const [settings, setSettings] = useState({
    initialMessageSettings,
  });

  const [errors, setErrors] = useState({
    initialMessageText: null,
    initialMessageUrl: null,
  });

  function handleUpdateInitialMessageSettings(updatedSetting) {
    setSettings((prevSettings) => {
      return {
        initialMessageSettings: {
          ...prevSettings.initialMessageSettings,
          ...updatedSetting,
        },
      };
    });
  }

  function handleSave() {
    if (
      !Boolean(settings.initialMessageSettings.initialMessageText) &&
      !Boolean(settings.initialMessageSettings.initialMessageURL) &&
      settings.initialMessageSettings.disclaimerEnabled
    ) {
      setErrors((prevErrors) => {
        return {
          ...prevErrors,
          initialMessageText: "Please provide a disclaimer message",
          initialMessageURL: "Please provide a recording URL",
        };
      });
      return;
    }

    updateVoiceSettings(settings);
  }

  const isSaveDisabled = Object.values(errors).some((value) => Boolean(value));

  return (
    <div className="flex flex-col border rounded-lg w-full">
      <div className="py-4 px-6 bg-gray-50 rounded-t-lg">
        <p className="text-base font-semibold text-gray-900">Call Disclaimer</p>
        <p className="text-sm mt-1 text-gray-500">
          Inform users about call recording, legal and compliance requirements.
        </p>
      </div>
      <div className="grid grid-cols-4 p-4 space-y-8">
        <div className="col-span-4 xl:col-span-3">
          <DisclaimerSettings
            initialMessageSettings={settings.initialMessageSettings}
            onUpdate={handleUpdateInitialMessageSettings}
            errors={errors}
            onUpdateError={setErrors}
            locked={locked}
          />
        </div>
      </div>

      <div className="bg-gray-50 rounded-b-lg px-4 py-3 text-right">
        {!locked && (
          <Button
            disabled={isSaveDisabled}
            onClick={handleSave}
          >
            Save
          </Button>
        )}
      </div>
    </div>
  );
}

export default function CallRecordingAndDisclaimerSettings(props) {
  return (
    <div className="flex flex-col gap-y-8 m-4 w-3/5 min-w-[600px]">
      <CallRecordingSettings {...props} />
      <CallDisclaimerSettings {...props} />
    </div>
  );
}
